import React, { useEffect, useRef, useState } from 'react';
import { Card, CardProps } from '@mui/material';
import { styled } from '@mui/material/styles';

const AnimatedCardStyled = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: 0,
  transform: 'translateY(20px)',
  '&.visible': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 24px rgba(0, 109, 117, 0.2)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
    transition: 'left 0.5s',
    zIndex: 1,
  },
  '&:hover::before': {
    left: '100%',
  },
}));

interface AnimatedCardProps extends CardProps {
  delay?: number;
  children: React.ReactNode;
}

export const AnimatedCard: React.FC<AnimatedCardProps> = ({ 
  children, 
  delay = 0,
  sx,
  ...props 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setIsVisible(true);
            }, delay);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [delay]);

  return (
    <AnimatedCardStyled
      ref={cardRef}
      className={isVisible ? 'visible' : ''}
      {...props}
      sx={{
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms, ...other`,
        ...sx,
      }}
    >
      {children}
    </AnimatedCardStyled>
  );
};

export default AnimatedCard;








